import { Auth } from '@andes/auth';
import { Plex } from '@andes/plex';
import { cacheStorage, Server } from '@andes/shared';
import { Component } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, of } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { environment } from './../environments/environment';
import { CommonNovedadesService } from './components/novedades/common-novedades.service';
import { IProfesional } from './interfaces/IProfesional';
import { AdjuntosService } from './modules/rup/services/adjuntos.service';
import { ModulosService } from './services/novedades/modulos.service';
import { ProfesionalService } from './services/profesional.service';
import { WebSocketService } from './services/websocket.service';
import { GoogleTagManagerService } from './shared/services/analytics.service';
import { HotjarService } from './shared/services/hotJar.service';
import { PROPERTIES } from './styles/properties';
@Component({
    selector: 'app',
    templateUrl: './app.component.html',
})

export class AppComponent {
    public formacionGrado;
    public profesional;
    public estado: [];
    public hoy = new Date();
    public foto: any;
    public tieneFoto = false;
    public usuario;
    private initStatusCheck() {
        if (environment.APIStatusCheck) {
            setTimeout(() => {
                this.server.get('/core/status', { params: null, showError: false, showLoader: false }).pipe(
                    finalize(() => this.initStatusCheck()))
                    .subscribe({
                        next: (data) => this.plex.updateAppStatus(data),
                        error: () => this.plex.updateAppStatus({ API: 'Error' })
                    });
            }, 100000);
        } else {
            this.plex.updateAppStatus({ API: 'OK' });
        }
    }

    public columns = [
        {
            key: 'profesion',
            label: 'Profesión',
        },
        {
            key: 'matricula',
            label: 'Matrícula',
        },
        {
            key: 'vencimiento',
            label: 'Vencimiento',
        },
        {
            key: 'estado',
            label: 'Estado',
        }
    ];

    public columnsPosgrado = [
        {
            key: 'profesion',
            label: 'Profesión',
        },
        {
            key: 'matricula',
            label: 'Matrícula',
        },
        {
            key: 'altas',
            label: 'Fin de alta',
        },
        {
            key: 'vencimiento',
            label: 'Vencimiento',
        },
        {
            key: 'estado',
            label: 'Estado',
        }
    ];

    public menuList = [];
    public tieneNovedades = false;
    private modulos$: Observable<any[]>;


    constructor(
        public plex: Plex,
        public server: Server,
        public auth: Auth,
        public ws: WebSocketService,
        public hotjar: HotjarService,
        public analyticsService: GoogleTagManagerService,
        private commonNovedadesService: CommonNovedadesService,
        public adjuntos: AdjuntosService,
        private modulosService: ModulosService,
        private profesionalService: ProfesionalService,
        public sanitizer: DomSanitizer,
    ) {
        // Inicializa la vista
        this.plex.updateTitle('ANDES | Apps de Salud');


        // Inicializa el chequeo de conectividad
        this.initStatusCheck();

        Object.keys(PROPERTIES).forEach(key => {
            document.documentElement.style.setProperty(`--${key}`, PROPERTIES[key]);
        });

        this.auth.session().subscribe((sesion) => {
            this.usuario = sesion.usuario;
            if (sesion.permisos) {
                this.checkPermissions();
            }
            if (this.auth.profesional) {
                this.profesionalService.getByID(this.auth.profesional).pipe(
                    switchMap((profesional: IProfesional) => {
                        this.profesional = profesional;
                        if (this.profesional) {
                            this.profesional.formacionGrado?.forEach(item => {
                                item['estado'] = this.verificarEstado(item);
                            });
                            return this.profesionalService.getFoto({ id: this.auth.profesional });
                        }
                        return of(null);
                    })
                ).subscribe(resp => {
                    if (resp) {
                        this.foto = this.sanitizer.bypassSecurityTrustResourceUrl('data:image/jpeg;base64,' + resp);
                        this.tieneFoto = true;
                    }
                });
            }
        });

        const token = this.auth.getToken();
        if (token) {
            this.ws.setToken(token);
            this.auth.setToken(token);
        }
    }

    public loggedIn() {
        return this.auth.getToken() !== undefined;
    }

    public showRibbon() {
        return environment.environmentName === 'demo' || environment.environmentName === 'testing';
    }

    public ribbonLabel() {
        return environment.environmentName.toUpperCase();
    }

    public ribbonType() {
        switch (environment.environmentName) {
            case 'produccion':
                return 'info';
            case 'demo':
                return 'success';
            case 'testing':
                return 'warning';
            case 'development':
                return 'info';
        }
    }

    private orderItems(items) {
        this.menuList = items.sort((a, b) => {
            const aHasSubmodules = a.submodulos && a.submodulos.length > 0;
            const bHasSubmodules = b.submodulos && b.submodulos.length > 0;

            if (!aHasSubmodules && bHasSubmodules) { return -1; }
            if (aHasSubmodules && !bHasSubmodules) { return 1; }

            if (!a.orden && !b.orden) { return 0; }
            if (!a.orden) { return 1; }
            if (!b.orden) { return -1; }

            return a.orden - b.orden;
        });
    }

    public checkPermissions() {
        const modulos = [];
        const modulosNovedades = [];
        this.menuList = [];
        this.menuList.push({ label: 'Página Principal', icon: 'home', route: '/inicio' });
        this.menuList.push({ label: 'Padrones', icon: 'magnify', route: '/puco' });

        if (this.auth.loggedIn()) {
            this.auth.organizaciones().subscribe(data => {
                if (data.length > 1) {
                    this.menuList = [{ label: 'Seleccionar Organización', icon: 'home', route: '/auth/select-organizacion' }, ...this.menuList];
                    this.plex.updateMenu(this.menuList);
                }
            });
        }
        this.modulos$ = this.modulosService.search({ activo: true }).pipe(
            cacheStorage('modulos-v1')
        );

        this.modulos$.subscribe(registros => {
            registros.forEach((modulo) => {
                const menuOption = {
                    id: modulo._id,
                    label: modulo.nombre,
                    icon: modulo.icono,
                    prefix: 'adi',
                    color: modulo.color,
                    submodulos: []
                };

                modulo.permisos.forEach((permiso, index) => {
                    if (this.auth.getPermissions(permiso).length > 0) {
                        if (modulos.indexOf(modulo._id) === -1) {
                            modulosNovedades.push(modulo._id);

                            if (modulo.submodulos && modulo.submodulos.length > 0) {
                                const submodulos = [];

                                modulo.submodulos = modulo.submodulos.filter(x => this.auth.getPermissions(x.permisos[0]).length > 0);
                                modulo.submodulos.forEach((submodulo, key) => {
                                    modulos.push(submodulo._id);

                                    if (modulo.nombre === 'FACTURACIÓN' && submodulo.nombre === 'Nominali-<br>zadas') {
                                        submodulo.linkAcceso = '/buscador/nominalizadas';
                                    }

                                    const menuOptionSub = {
                                        id: submodulo._id,
                                        label: submodulo.nombre.replace(/<[^>]*>?/gm, ' ').replace('- ', ''),
                                        icon: submodulo.icono,
                                        route: submodulo.linkAcceso
                                    };

                                    submodulos.push(menuOptionSub);
                                });

                                menuOption.submodulos = submodulos;

                                if (this.menuList.findIndex(x => x.id === menuOption.id) === -1) {
                                    this.menuList.push(menuOption);
                                }
                            } else {
                                const subMenuOption = {
                                    id: index,
                                    label: modulo.subtitulo,
                                    route: modulo.linkAcceso,
                                    icon: modulo.icono,
                                    prefix: 'adi'
                                };

                                this.menuList.push(subMenuOption);
                            }

                            this.orderItems(this.menuList);
                        }
                    }
                });
            });
            if (modulosNovedades.length) {
                this.commonNovedadesService.setNovedadesSinFiltrar(modulosNovedades);
                this.commonNovedadesService.getNovedadesSinFiltrar().subscribe((novedades) => {
                    this.tieneNovedades = novedades.length > 0;
                });
            }
            this.menuList.push({ divider: true });
            this.menuList.push({ label: 'Cerrar Sesión', icon: 'logout', route: '/auth/logout' });
            this.plex.updateMenu(this.menuList);
        });
    }

    public getModulos() {
        return this.modulos$;
    }

    verificarEstado(formacionGrado) {
        if (formacionGrado.matriculacion) {
            if (!formacionGrado.matriculado && this.hoy < formacionGrado.matriculacion[formacionGrado.matriculacion.length - 1].fin) {
                return {
                    nombre: 'suspendida',
                    tipo: 'warning'
                };
            } else {
                if (this.hoy > formacionGrado.matriculacion[formacionGrado.matriculacion.length - 1].fin) {
                    return {
                        nombre: 'vencida',
                        tipo: 'danger'
                    };
                } else {
                    return {
                        nombre: 'vigente',
                        tipo: 'success'
                    };
                }
            }
        }
    }

    vencimientoAlta(fecha: Date): Date {
        const nuevaFecha = new Date(fecha);
        nuevaFecha.setFullYear(nuevaFecha.getFullYear() + 5);
        return nuevaFecha;
    }

    verificarFecha(formacionPosgrado) {
        if (formacionPosgrado?.matriculacion?.length) {
            if (!formacionPosgrado.matriculado) {
                return 'suspendida';
            } else {
                if (!formacionPosgrado.tieneVencimiento) {
                    return 'sinVencimiento';
                } else {
                    const ultMat = formacionPosgrado.matriculacion.length - 1;
                    const ultPer = formacionPosgrado.matriculacion[ultMat].periodos.length - 1;
                    if (this.hoy > formacionPosgrado.matriculacion[ultMat].periodos[ultPer].fin) {
                        return 'vencida';
                    } else {
                        return 'vigente';
                    }
                }
            }
        }
    }
}
